import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { canAfford, isPlanned, type ProjectSummary, type WeekItem } from "@joogoos/contracts";
import { api } from "../api.js";
import { useWorkspace } from "../ui/workspace.js";
import { Icon } from "../ui/icons.js";

/**
 * Prepare my week, at the top of the Studio rail (docs/03 §prepare my week, ADR 0023).
 *
 * With nothing planned it is one button, with what planning costs under it. With a week planned it is the week: a
 * row a day, each opening its piece in the Studio, where the chat says why this and Generate carries its price — and
 * one button that presses Generate on all of them, with the whole week's price on it and asked once before it runs.
 *
 * The week is asked for again whenever a piece of it changes in the rail's own list — made, dropped, or its chat
 * answered — so it is never a second list that has to be kept in step by hand.
 */
export function Week({ projects, activeId, onPick, onDelete }: {
  projects: ProjectSummary[] | undefined;
  activeId: string | null;
  onPick: (id: string) => void;
  onDelete?: ((id: string) => void) | undefined;
}) {
  const qc = useQueryClient();
  const planned = useMemo(() => (projects ?? []).filter(isPlanned), [projects]);
  const signature = planned.map((p) => `${p.id}:${p.updatedAt}`).join(",");
  const week = useQuery({ queryKey: ["week", signature], queryFn: api.week, enabled: projects !== undefined });
  const ws = useWorkspace();
  const refresh = () => { for (const key of ["projects", "week", "calendar", "workspace"]) void qc.invalidateQueries({ queryKey: [key] }); };
  const plan = useMutation({ mutationFn: api.planWeek, onSuccess: refresh });
  const make = useMutation({ mutationFn: api.makeWeek, onSuccess: () => { setAsking(false); refresh(); } });
  const [asking, setAsking] = useState(false);

  const items = week.data?.items ?? [];
  if (!items.length) {
    const cost = week.data?.planUsd;
    return (
      <div className="px-3 pt-1 pb-2 flex flex-col gap-1.5">
        <button
          onClick={() => plan.mutate()}
          disabled={plan.isPending}
          className="flex items-center justify-center gap-2 py-2 rounded-lg border border-line bg-panel text-[13px] font-medium hover:bg-page disabled:opacity-50"
        >
          <Icon name="calendar" size={14} /> {plan.isPending ? "Planning your week…" : "Prepare my week"}
        </button>
        <p className="text-[11px] text-muted leading-snug px-0.5">
          {plan.isPending
            ? "A post a day from your photos and your Business page. It takes a few seconds."
            : `${cost ? `About $${cost} to plan. ` : ""}Nothing is made until you say; each post shows its price first.`}
        </p>
        {plan.error ? <p role="alert" className="text-[11px] text-bad leading-snug px-0.5">{plan.error.message}</p> : null}
      </div>
    );
  }

  const ready = items.filter((i) => i.ready);
  const total = week.data!.totalUsd;
  const money = ws.data ? canAfford(total, ws.data) : { ok: true as const };
  return (
    <div className="pb-1">
      <div className="px-3.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[.08em] text-coral">Your week · {items.length}</div>
      {items.map((item) => <Day key={item.projectId} item={item} on={item.projectId === activeId} onPick={onPick} onDelete={onDelete} />)}
      <div className="px-3 pt-2 flex flex-col gap-1.5">
        {!ready.length ? null : !money.ok ? (
          <p className="text-[11px] text-muted leading-snug px-0.5">
            Making all {ready.length} needs about ${total}, and ${money.leftUsd} is left{money.reason === "credit" ? "" : " of your limit"}. Each can still be made on its own.
          </p>
        ) : asking ? (
          <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-panel px-2.5 py-2">
            <span className="text-[12px] leading-snug">Make all {ready.length} for about ${total}? Each is charged as it is made.</span>
            <span className="flex gap-1.5">
              <button onClick={() => make.mutate()} disabled={make.isPending} className="px-2.5 py-1 rounded-md bg-coral text-white text-[11.5px] font-semibold disabled:opacity-50">
                {make.isPending ? "Starting…" : "Make them"}
              </button>
              <button onClick={() => setAsking(false)} className="px-2 py-1 rounded-md text-[11.5px] text-muted hover:text-ink">Not yet</button>
            </span>
          </div>
        ) : (
          <button onClick={() => setAsking(true)} className="py-2 rounded-lg bg-ink text-white text-[12.5px] font-medium hover:opacity-90">
            Make all {ready.length} · ${total}
          </button>
        )}
        {make.error ? <p role="alert" className="text-[11px] text-bad leading-snug px-0.5">{make.error.message}</p> : null}
      </div>
    </div>
  );
}

/** One day of the week: the day, the piece, and its price. Dropping it asks first, in place, as the rail's rows do. */
function Day({ item, on, onPick, onDelete }: { item: WeekItem; on: boolean; onPick: (id: string) => void; onDelete?: ((id: string) => void) | undefined }) {
  const [asking, setAsking] = useState(false);
  const day = new Date(item.planFor).toLocaleDateString(undefined, { weekday: "short", day: "numeric" });

  if (asking && onDelete) {
    return (
      <div className="mx-1.5 w-[calc(100%-0.75rem)] flex items-center gap-2 px-2.5 py-1.5 rounded-[9px] bg-panel border border-line">
        <span className="min-w-0 flex-1 text-[12px] truncate">Drop {day}?</span>
        <button onClick={() => { setAsking(false); onDelete(item.projectId); }} className="px-2 py-1 rounded-md bg-bad text-white text-[11px] font-semibold">Drop</button>
        <button onClick={() => setAsking(false)} className="px-2 py-1 rounded-md text-[11px] text-muted hover:text-ink">Keep</button>
      </div>
    );
  }

  return (
    <div className="group relative">
      <button
        onClick={() => onPick(item.projectId)}
        className={`w-full flex items-center gap-2.5 mx-1.5 px-2.5 py-1.5 rounded-[9px] text-left w-[calc(100%-0.75rem)] ${
          on ? "bg-panel border border-line shadow-[0_1px_2px_rgba(23,23,26,.08)]" : "hover:bg-panel/60"
        }`}
      >
        <span className="w-7 h-7 rounded-md border border-dashed border-line-2 grid place-items-center text-muted shrink-0">
          <Icon name={item.kind === "post" ? "image" : "play"} size={12} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-[12.5px] truncate ${on ? "font-semibold" : "font-medium"}`}>{item.title}</span>
          <span className="block text-[10.5px] text-muted">{day} · {item.ready ? `$${item.usd}` : "open it to finish"}</span>
        </span>
      </button>
      {onDelete ? (
        <button
          onClick={() => setAsking(true)}
          title="Drop this day"
          aria-label={`Drop ${item.title}`}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-md text-faint hover:text-bad hover:bg-page opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100"
        >
          <Icon name="x" size={13} />
        </button>
      ) : null}
    </div>
  );
}
